/**
 * Breakdowns for any stretch of time ("this month", "last 90 days", or from-to), and the same
 * breakdowns month by month for the graph view. Same rules as the ledger's all-time insights.
 */
import type { Breakdown, ComputedItem, Ledger } from "./ledger.ts";
import type { GroupState, IsoDate } from "../types.ts";
import { monthsBetween } from "./trends.ts";

export type RangePreset = "month" | "last-month" | "90d" | "year" | "all" | "custom";

export interface DateRange {
  /** Inclusive; null means no limit. */
  from: IsoDate | null;
  to: IsoDate | null;
}

function iso(d: Date) {
  return d.toISOString().slice(0, 10);
}

/** The dates a preset covers, relative to today. */
export function presetRange(preset: RangePreset, today: IsoDate, custom?: DateRange): DateRange {
  const d = new Date(`${today}T12:00:00Z`);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  switch (preset) {
    case "month":
      return { from: iso(new Date(Date.UTC(y, m, 1))), to: today };
    case "last-month":
      return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
    case "90d": {
      const f = new Date(d);
      f.setUTCDate(f.getUTCDate() - 89);
      return { from: iso(f), to: today };
    }
    case "year":
      return { from: `${y}-01-01`, to: today };
    case "custom":
      return custom ?? { from: null, to: null };
    default:
      return { from: null, to: null };
  }
}

export type Dimension = "store" | "category" | "person" | "card";

export interface RangeBreakdowns {
  byStore: Breakdown[];
  byCategory: Breakdown[];
  byPerson: Breakdown[];
  byCard: Breakdown[];
  totalMicro: number;
  /** Months covered, "YYYY-MM", for the graph view. */
  months: string[];
}

const inRange = (c: ComputedItem, r: DateRange) =>
  (!r.from || c.item.date >= r.from) && (!r.to || c.item.date <= r.to);

/**
 * What one item adds to each line of a breakdown. With `who`, it's that person's part instead
 * of the whole item, and "person" means who they split it with.
 */
function keysOf(
  c: ComputedItem,
  dim: Dimension,
  state: GroupState,
  who?: string,
): [string, number][] {
  if (who) {
    const mine = c.shares[who];
    if (mine === undefined) return [];
    if (dim !== "person") return keysOf({ ...c, totalMicro: mine }, dim, state);
    return c.item.ticked
      .filter((pid) => pid !== who && state.people[pid])
      .map((pid) => [state.people[pid]!.name, mine]);
  }
  switch (dim) {
    case "store":
      return [[c.item.store, c.totalMicro]];
    case "category":
      return [[c.category, c.totalMicro]];
    case "card":
      return [[c.item.paidWith ?? "Not set", c.totalMicro]];
    case "person":
      return Object.entries(c.shares)
        .filter(([pid]) => state.people[pid])
        .map(([pid, v]) => [state.people[pid]!.name, v]);
  }
}

const sorted = (m: Map<string, number>): Breakdown[] =>
  [...m.entries()]
    .map(([label, totalMicro]) => ({ label, totalMicro }))
    .sort((a, b) => b.totalMicro - a.totalMicro);

export function rangeBreakdowns(
  state: GroupState,
  ledger: Ledger,
  range: DateRange,
  /** Only this person's part of each item (a member's own view). */
  who?: string,
): RangeBreakdowns {
  const maps: Record<Dimension, Map<string, number>> = {
    store: new Map(),
    category: new Map(),
    person: new Map(),
    card: new Map(),
  };
  let total = 0;
  let first: string | null = null;
  let last: string | null = null;
  for (const c of ledger.items) {
    if (c.category === "Balance" || !inRange(c, range)) continue;
    if (who && c.shares[who] === undefined) continue;
    total += who ? c.shares[who]! : c.totalMicro;
    const mo = c.item.date.slice(0, 7);
    if (!first || mo < first) first = mo;
    if (!last || mo > last) last = mo;
    for (const dim of ["store", "category", "person", "card"] as Dimension[])
      for (const [k, v] of keysOf(c, dim, state, who))
        maps[dim].set(k, (maps[dim].get(k) ?? 0) + v);
  }
  return {
    byStore: sorted(maps.store),
    byCategory: sorted(maps.category),
    byPerson: sorted(maps.person),
    byCard: sorted(maps.card),
    totalMicro: total,
    months:
      first && last
        ? monthsBetween(range.from?.slice(0, 7) ?? first, range.to?.slice(0, 7) ?? last)
        : [],
  };
}

/** One dimension month by month: the top `top` labels each get a series, the rest are "Other". */
export function monthlyBy(
  state: GroupState,
  ledger: Ledger,
  range: DateRange,
  dim: Dimension,
  months: readonly string[],
  top = 5,
): { name: string; values: number[] }[] {
  const totals = new Map<string, number>();
  const grid = new Map<string, number[]>();
  const idx = new Map(months.map((m, i) => [m, i]));
  for (const c of ledger.items) {
    if (c.category === "Balance" || !inRange(c, range)) continue;
    const i = idx.get(c.item.date.slice(0, 7));
    if (i === undefined) continue;
    for (const [k, v] of keysOf(c, dim, state)) {
      totals.set(k, (totals.get(k) ?? 0) + v);
      const row = grid.get(k) ?? months.map(() => 0);
      row[i]! += v;
      grid.set(k, row);
    }
  }
  const order = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  const out = order.slice(0, top).map((name) => ({ name, values: grid.get(name)! }));
  const rest = order.slice(top);
  if (rest.length) {
    out.push({
      name: `${rest.length} more`,
      values: months.map((_, i) => rest.reduce((s, k) => s + grid.get(k)![i]!, 0)),
    });
  }
  return out;
}

/**
 * Time steps for a graph across a range: days for up to about six weeks, weeks for up to about
 * six months, months beyond that. `index(date)` says which step a date falls in.
 */
export function timeSteps(
  from: IsoDate,
  to: IsoDate,
): { unit: "day" | "week" | "month"; starts: IsoDate[]; index: (date: IsoDate) => number } {
  const DAY = 86_400_000;
  const t0 = Date.parse(`${from}T12:00:00Z`);
  const days = Math.max(1, Math.round((Date.parse(`${to}T12:00:00Z`) - t0) / DAY) + 1);
  const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
  if (days <= 45) {
    const starts = Array.from({ length: days }, (_, i) => iso(t0 + i * DAY));
    return {
      unit: "day",
      starts,
      index: (d) => Math.round((Date.parse(`${d}T12:00:00Z`) - t0) / DAY),
    };
  }
  if (days <= 190) {
    const n = Math.ceil(days / 7);
    const starts = Array.from({ length: n }, (_, i) => iso(t0 + i * 7 * DAY));
    return {
      unit: "week",
      starts,
      index: (d) => Math.floor((Date.parse(`${d}T12:00:00Z`) - t0) / (7 * DAY)),
    };
  }
  const months: IsoDate[] = [];
  const d = new Date(`${from.slice(0, 7)}-01T12:00:00Z`);
  while (iso(d.getTime()).slice(0, 7) <= to.slice(0, 7)) {
    months.push(iso(d.getTime()));
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  const at = new Map(months.map((m, i) => [m.slice(0, 7), i]));
  return { unit: "month", starts: months, index: (x) => at.get(x.slice(0, 7)) ?? -1 };
}

/**
 * One dimension over the range as running totals per step (for line graphs): the top `top`
 * labels get a line each and the rest add up into "N more". Works for any range, even a month.
 */
export function runningBy(
  state: GroupState,
  ledger: Ledger,
  range: DateRange,
  dim: Dimension,
  today: IsoDate,
  top = 5,
  who?: string,
): {
  unit: "day" | "week" | "month";
  starts: IsoDate[];
  series: { name: string; values: number[] }[];
} {
  const inside = ledger.items.filter((c) => c.category !== "Balance" && inRange(c, range));
  const first =
    range.from ??
    inside.reduce<IsoDate | null>((m, c) => (!m || c.item.date < m ? c.item.date : m), null) ??
    today;
  const last = range.to ?? today;
  const steps = timeSteps(first, last < first ? first : last);
  const n = steps.starts.length;
  const totals = new Map<string, number>();
  const grid = new Map<string, number[]>();
  for (const c of inside) {
    const i = steps.index(c.item.date);
    if (i < 0 || i >= n) continue;
    for (const [k, v] of keysOf(c, dim, state, who)) {
      totals.set(k, (totals.get(k) ?? 0) + v);
      const row = grid.get(k) ?? new Array<number>(n).fill(0);
      row[i]! += v;
      grid.set(k, row);
    }
  }
  const run = (row: number[]) => {
    let t = 0;
    return row.map((v) => (t += v));
  };
  const order = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  const series = order.slice(0, top).map((name) => ({ name, values: run(grid.get(name)!) }));
  const rest = order.slice(top);
  if (rest.length)
    series.push({
      name: `${rest.length} more`,
      values: run(
        new Array<number>(n).fill(0).map((_, i) => rest.reduce((s, k) => s + grid.get(k)![i]!, 0)),
      ),
    });
  return { unit: steps.unit, starts: steps.starts, series };
}
